import React from 'react';
import PropTypes from 'prop-types';
import {pick} from 'lodash';
import {withRouter} from 'react-router';
import {OrderedMap, List} from 'immutable';
import {Button, Icon, Popover} from '@albert-io/atomic';

import {pluralize} from 'lib/stringUtils';

import {pushQueryParams, removeQueryParams} from 'client/history';

import StandardsSubSearch from './StandardsSubSearch/StandardsSubSearch.react';
import TagsSubSearch from './TagsSubSearch/TagsSubSearch.react';
import {KeywordSearch} from './KeywordSearch/KeywordSearch.react';

import {
  getStandardsByIdQuery,
  getTagsByIdQuery,
  hasStandardsQuery,
  hasTagsQuery
} from './AdvancedSearch.queries';

import './advanced-search.scss';

/**
 * AdvancedSearch
 * ===
 * This component wraps up a configuration of atomic and application components for usage
 * in the Subject View's Study Guide tab. It serves as an interface layer between the raw
 * data we receive when we bootstrap the Study Guide and the atomic components that consume it.
 *
 * AdvancedSearch mutates its props into the form these components expect before administering them.
 *
 * At the same time, it receives back input from the FindAndApply instances among its children and
 * re-formats that feedback into query params that our search toolkit understands.
 *
 * Props:
 *  subjectId - the ID of the subject to which our search is scoped
 *  standards - an immutable list of the resource entities of the standards (FKA labels)
 *    by which we are filtering our search results
 *  tags - an immutable list of the resource entities of the tags by which we are filtering
 *    our search results
 *  searchString - an iterable of the phrases and tokens by which we are filtering
 *    our search results
 */
const filterableResourceQueries = {
  standards: getStandardsByIdQuery,
  tags: getTagsByIdQuery
};

// This method re-formats our filterable resources into maps keyed by each
// entity's ID, reducing them to a format compatible with the FindAndApply
// instances among the children.
function mapResourceByKey(resourceList) {
  return resourceList.toOrderedMap().mapKeys((key) => resourceList.get(key).getId());
}

const AdvancedSearchBtn = ({children, ...rest}) => (
  <Button {...rest} className='advanced-search__btn' variant='outlined' color='secondary'>
    {children}
    <Icon className='advanced-search__btn-icon u-mar-l_1' icon='caret-down' />
  </Button>
);

AdvancedSearchBtn.propTypes = {
  children: PropTypes.node
};

const popoverModifier = {
  flip: {
    enabled: false
  }
};

class AdvancedSearch extends React.Component {
  static propTypes = {
    location: PropTypes.shape({
      query: PropTypes.shape({
        s: PropTypes.string,
        standards: PropTypes.string,
        tags: PropTypes.string
      }),
      pathname: PropTypes.string,
      search: PropTypes.string
    }),
    onSearchCallback: PropTypes.func,
    subjectId: PropTypes.string.isRequired
  };

  static defaultProps = {
    onSearchCallback: () => {}
  };

  constructor(props) {
    super(props);
    this.state = {
      error: List(), // @todo: Display toasts for errors
      showStandards: false,
      showTags: false,
      standards: OrderedMap(),
      tags: OrderedMap(),
      searchString: props.location ? props.location.query.s : '',
      guideType: props.location.pathname.includes('assessments') ? 'assessment' : 'practice'
    };

    this.standardsBtnRef = React.createRef();
    this.tagsBtnRef = React.createRef();
  }

  componentDidMount() {
    const {query} = this.props.location;

    // Reduce our query object down to the params we care about
    const params = pick(query, Object.keys(filterableResourceQueries));

    // If we're in an active search, update the child SubSearch components with the appropriate defaults
    if (Object.keys(params).length) {
      this.fetchData(params);
    }
  }

  componentDidUpdate(prevProps) {
    // Verify whether there are changes to the outer standards or tags props we must register with state
    const hasChanges = this.props.location.search !== prevProps.location.search;
    if (hasChanges) {
      const params = pick(this.props.location.query, Object.keys(filterableResourceQueries));
      this.fetchData(params);
    }
  }

  async fetchData(params) {
    // eslint-disable-next-line guard-for-in
    for (const resourceType in params) {
      const ids = params[resourceType];
      if (!ids) {
        this.setState(() => ({[resourceType]: OrderedMap()}));
        // eslint-disable-next-line no-continue
        continue;
      }
      try {
        // eslint-disable-next-line no-await-in-loop
        const resource = await filterableResourceQueries[resourceType](ids).getResourcePromise();
        this.setState(() => ({[resourceType]: mapResourceByKey(resource)}));
      } catch (e) {
        this.setState(({error}) => ({error: error.push(e)}));
      }
    }
  }

  updateSearchParams({standards, search, tags}) {
    // @todo: Add properties representing tags and questionSearchString to this object
    pushQueryParams({
      ...(typeof search === 'string' && {s: search}),
      ...(standards && {standards: standards.keySeq().join(',')}),
      ...(tags && {tags: tags.keySeq().join(',')})
    });
    this.props.onSearchCallback();
  }

  handleSearchSubmit = (searchString) => {
    this.updateSearchParams({search: searchString});
  };

  handleSearchReset = () => removeQueryParams('s');

  toggleStandards = () =>
    this.setState(({showStandards}) => ({showStandards: !showStandards, showTags: false}));

  toggleTags = () => this.setState(({showTags}) => ({showStandards: false, showTags: !showTags}));

  hasResults(query) {
    return (
      query.isResourcePopulated() &&
      query.getResourceMetadata().getIn(['page', 'total_entries'], 0) > 0
    );
  }

  hasTags() {
    return this.hasResults(hasTagsQuery(this.props.subjectId));
  }

  hasStandards() {
    return this.hasResults(hasStandardsQuery(this.props.subjectId));
  }

  render() {
    // These could also be helper funcs
    const standardsBtnLabel = !this.state.standards.isEmpty()
      ? `${this.state.standards.size} ${pluralize('standard', this.state.standards.size)}`
      : 'Standards';
    const tagsBtnLabel = !this.state.tags.isEmpty()
      ? `${this.state.tags.size} ${pluralize('tag', this.state.tags.size)}`
      : 'Tags';
    return (
      <div className='advanced-search'>
        {/* @todo: Add A11y support autofocusing on subsearch search field upon toggling */}
        {this.hasStandards() && (
          <span className='advanced-search__btn-wrapper' ref={this.standardsBtnRef}>
            <AdvancedSearchBtn
              aria-expanded={this.state.showStandards}
              onClick={this.toggleStandards}
            >
              {standardsBtnLabel}
            </AdvancedSearchBtn>
            {this.state.showStandards && (
              <Popover
                usePortal={false}
                border='none'
                className='advanced-search__popover'
                expanded={this.state.showStandards}
                paddingLevel={null}
                targetRef={this.standardsBtnRef}
                modifiers={popoverModifier}
              >
                <StandardsSubSearch
                  guideType={this.state.guideType}
                  className='advanced-search__sub-search advanced-search__sub-search--standards'
                  defaultSelectedItems={this.state.standards}
                  subjectId={this.props.subjectId}
                  onApply={(standards) => {
                    this.updateSearchParams({standards});
                    this.setState({showStandards: false});
                  }}
                  onCancel={() => this.setState({showStandards: false})}
                />
              </Popover>
            )}
          </span>
        )}
        {/* @todo: Add A11y support autofocusing on subsearch search field upon toggling */}
        {this.hasTags() && (
          <span className='advanced-search__btn-wrapper' ref={this.tagsBtnRef}>
            <AdvancedSearchBtn aria-expanded={this.state.showTags} onClick={this.toggleTags}>
              {tagsBtnLabel}
            </AdvancedSearchBtn>
            {this.state.showTags && (
              <Popover
                usePortal={false}
                border='none'
                className='advanced-search__popover'
                expanded={this.state.showTags}
                paddingLevel={null}
                targetRef={this.tagsBtnRef}
                modifiers={popoverModifier}
              >
                <TagsSubSearch
                  guideType={this.state.guideType}
                  defaultSelectedItems={this.state.tags}
                  onApply={(tags) => {
                    this.updateSearchParams({tags});
                    this.setState({showTags: false});
                  }}
                  onCancel={() => this.setState({showTags: false})}
                  subjectId={this.props.subjectId}
                />
              </Popover>
            )}
          </span>
        )}
        <KeywordSearch
          aria-label='Search question text'
          className='advanced-search__search-input'
          defaultValue={this.state.searchString}
          onReset={this.handleSearchReset}
          onChange={this.handleSearchSubmit}
          placeholder='Search question text'
        />
      </div>
    );
  }
}

export default withRouter(AdvancedSearch);
